export const SOLUTIONS_EN = [
    {
        slug: "developpement-international",
        num: "01",
        title: "International development",
        tagline: "From strategy to field execution.",
        heroDesc:
            "WEYB supports your international growth across the entire journey: diagnosis, strategy, market studies, prospecting, trade fairs, closing and management.",
        image: "/assets/terrain-b2b-roseblanche.jpg",
        imageAspect: "aspect-[3/4]",
        imageAlt: "B2B meeting at an international trade fair — WEYB mission",
        problematique:
            "International expansion cannot be improvised. Choosing markets, understanding local practices, identifying the right partners, being present in the field: without method and support, export initiatives scatter and consume resources without producing lasting results.",
        pourQui: [
            "SME and startup leaders",
            "Export and sales managers",
            "First-time exporters",
            "Exporters looking to accelerate",
            "Private-sector support organisations",
            "Institutions and international programmes",
        ],
        actions: [
            { title: "Export diagnosis", desc: "A complete assessment of your potential, capabilities and international readiness." },
            { title: "Export strategy", desc: "Defining objectives, positioning and the international roadmap." },
            { title: "Market prioritisation", desc: "Ranking target markets by their real potential for your offer." },
            { title: "Market studies", desc: "Studies, monitoring and competitive analysis to decide on factual grounds." },
            { title: "B2B prospecting", desc: "Identifying and qualifying partners, distributors and target clients." },
            { title: "Sales closing", desc: "Support through to signature: negotiation, follow-up, securing agreements." },
            { title: "International trade fairs", desc: "Preparation, meeting targeting and concrete exploitation of your participation." },
            { title: "B2B missions", desc: "Organisation and support of trade missions in your target markets." },
            { title: "Field support", desc: "Operational presence by your side, in the field, when it matters." },
            { title: "Monitoring & management", desc: "Indicators, pipeline and continuous management of your export development." },
        ],
        livrables: [
            "A documented export diagnosis",
            "A roadmap with priority markets",
            "Market studies and analyses",
            "A pipeline of qualified prospects and partners",
            "A trade fair & mission action plan",
            "Export management dashboards",
        ],
        faq: [
            {
                q: "Which companies is this pillar designed for?",
                a: "For SMEs and startups, whether first-time exporters or established exporters looking to accelerate, as well as private-sector support organisations, institutions and international programmes.",
            },
            {
                q: "Which regions do you operate in?",
                a: "WEYB operates primarily in Europe, Africa and the MENA region, from its base in France, its subsidiary in Tunisia and a network of international partners and experts.",
            },
            {
                q: "How do you differ from a prospecting agency?",
                a: "WEYB is neither a marketing agency nor a mere prospecting company: we support the full journey, from strategy to field execution, with Export360 technology and human support.",
            },
            {
                q: "How do we start?",
                a: "With a first 30-minute call with Saber Bellaaj, free of commitment, to review your ambitions, target markets and the first actions to launch.",
            },
        ],
    },
    {
        slug: "programmes-appui-formation",
        num: "02",
        title: "Support Programs & Capacity Building",
        tagline: "Design, train, support, implement, measure.",
        heroDesc:
            "WEYB designs and implements internationalisation support programmes for support organisations, institutions and development partners: engineering, ToT, training, coaching, M&E.",
    },
    {
        slug: "export360",
        num: "03",
        title: "Export360",
        tagline: "The platform that structures and manages your export development.",
        heroDesc:
            "Developed by WEYB, Export360 centralises diagnostics, market prioritisation, AI-assisted studies, e-learning and the management of your international operations.",
        dark: true,
        image: "/assets/export360-screenshot.webp",
        imageAspect: "aspect-[16/10]",
        imageAlt: "The Export360 platform — export management system",
        externalCta: { label: "Discover Export360.fr", href: "https://export360.fr/" },
        problematique:
            "In many companies, export development is scattered across spreadsheets, notes and emails: impossible to manage, to train teams or to capitalise on studies. Export360 was designed to bring all of this together in one place.",
        pourQui: [
            "Leaders and export managers",
            "Sales teams needing structure",
            "First-time exporters",
            "Organisations supporting companies",
        ],
        actions: [
            { title: "Diagnostics", desc: "Assess your export readiness and identify your priorities for progress." },
            { title: "Market prioritisation", desc: "Compare and rank your target markets with dedicated tools." },
            { title: "Management tools", desc: "Track your actions, pipeline and export indicators in one place." },
            { title: "AI-assisted studies", desc: "Accelerate your market studies with artificial intelligence." },
            { title: "Trade fairs & missions", desc: "Prepare your international events: objectives, meetings, follow-up." },
            { title: "Templates", desc: "Ready-to-use documents and frameworks to structure your approach." },
            { title: "E-learning & quizzes", desc: "Train your teams in international trade, with quizzes and a leaderboard." },
            { title: "AI chatbot", desc: "An assistant to answer your export questions every day." },
            { title: "Studies marketplace", desc: "Access market studies and specialised resources." },
            { title: "Coaches & consultants", desc: "Access experts to complement the platform with human support." },
            { title: "Business opportunities", desc: "Identify and track concrete business opportunities." },
        ],
        livrables: [
            "A structured export management workspace",
            "Diagnostics and priority market plans",
            "Training paths for your teams",
            "Framed preparation of your trade fairs and missions",
        ],
        faq: [
            {
                q: "What is the difference between WEYB and Export360?",
                a: "WEYB is the human consulting firm; Export360 is the technology platform developed by WEYB to structure and manage export development. The two are complementary but distinct.",
            },
            {
                q: "Can Export360 be used without WEYB support?",
                a: "Yes, the platform works autonomously. It also provides access to coaches and consultants whenever you want human support.",
            },
            {
                q: "Who is the platform designed for?",
                a: "For leaders, export managers and sales teams who want to structure their approach, as well as organisations supporting companies internationally.",
            },
            {
                q: "How can we discover the platform?",
                a: "Visit export360.fr to discover the platform, or book a 30-minute call for a presentation adapted to your situation.",
            },
        ],
    },
    {
        slug: "implantation-rh-international",
        num: "04",
        title: "International setup & HR",
        tagline: "Your local presence, without the friction.",
        heroDesc:
            "Entity creation, recruitment, payroll solutions and operational support: WEYB helps you establish a lasting presence in your target markets.",
        problematique:
            "Setting up in a new market goes far beyond sales: legal entity, local recruitment, administrative and accounting management, team organisation… Critical topics that, poorly anticipated, slow down or weaken your development.",
        pourQui: [
            "Companies looking to establish a presence in new markets",
            "SMEs without a dedicated international HR department",
            "Exporters moving from selling to local presence",
            "Leaders seeking reliable operational support",
        ],
        actions: [
            { title: "International setup", desc: "Strategy and execution of your establishment in a new market." },
            { title: "Entity creation", desc: "Setting up the appropriate legal and operational structure." },
            { title: "Recruitment", desc: "Identifying and recruiting the local talent you need." },
            { title: "Payroll solutions", desc: "Employ locally without immediately creating a legal entity." },
            { title: "Team setup", desc: "Building and upskilling your local teams." },
            { title: "Administrative support", desc: "Managing the administrative obligations of your local presence." },
            { title: "Accounting", desc: "Bookkeeping and accounting adapted to the country of establishment." },
            { title: "Facility management", desc: "Managing the premises and general services of your local office." },
            { title: "Operational support", desc: "Continuous assistance to secure your day-to-day operations on site." },
        ],
        livrables: [
            "A compliant operational structure",
            "Your first talents recruited",
            "Outsourced administrative and accounting management",
            "A single point of contact for your establishment",
        ],
        faq: [
            {
                q: "What is international payroll outsourcing?",
                a: "Payroll solutions let you employ a team member in a foreign market without immediately creating a legal entity there: a flexible way to test a market or start quickly.",
            },
            {
                q: "In which regions do you offer these services?",
                a: "WEYB relies on its base in France, its subsidiary in Tunisia and its network of international partners, with priority experience in Europe, Africa and the MENA region.",
            },
            {
                q: "Can setup and business development be combined?",
                a: "Yes, and it is even recommended: local establishment naturally extends successful business development. WEYB's pillars work together across the entire journey.",
            },
            {
                q: "Where do we start?",
                a: "With a 30-minute call with Saber Bellaaj to assess your establishment project, your constraints and a realistic timeline.",
            },
        ],
    },
];
